/**
 * One project's screen: what is in it, putting more in and taking some out,
 * and renaming, archiving and unarchiving it.
 *
 * The reads are `GET /api/groups/:id/projects/:projectId` (the project, its
 * count and whether it is settled), `GET .../transactions?projectId=` (what
 * is in it) and the group's own transactions, which are what Add
 * transactions offers. Nothing here moves money — a project is a label — so
 * none of the writes is optimistic; each waits for its answer.
 *
 * **Archiving asks first when the project is not settled.** Archiving locks a
 * project; it does not settle it, and putting away a project that still has
 * money owing in it is the kind of thing somebody does by accident and
 * notices a month later. So the first press says "balances in this project
 * are not zero" and the second one archives — the same two-press shape every
 * other consequential button in this app has. A settled one archives on the
 * first press.
 *
 * An archived project is locked, as the server has it: no Add transactions,
 * no Remove, no Rename, no Settle up — only Unarchive.
 *
 * **Settling up happens here too.** Where everybody stands in the project and
 * who pays whom to clear it are read from the project's own endpoints
 * (`.../projects/:id/balances` and `.../settlement-plan`), never the group's:
 * the group's plan nets everything the group has spent, and is not what this
 * project owes. Each suggested payment opens the same `SettleUp` dialog the
 * group's plan does, and what it writes is the same optimistic repayment with
 * the project's id on the draft — so it is on record what the payment was
 * for, and, a project being a view of the group's ledger, it moves the
 * group's balances as well. Once the project nets to zero it says *Settled*
 * and suggests archiving it.
 */

import { useState, type FormEvent } from 'react'
import { newIdempotencyKey, type Payment } from '../api'
import { Amount, AmountChip } from '../components/Amount'
import { SettleUp } from '../components/SettleUp'
import { TransactionPicker } from '../components/TransactionPicker'
import { describeMember, formatDay, formatMoney, describeYourStake } from '../lib/format'
import {
  archiveWarning,
  describeProjectCount,
  pickerRows,
  projectNameProblem,
  projectSettleUp,
  tickedIds,
  MAX_PROJECT_NAME,
} from '../lib/projects'
import { describePlanShape, settlementDraft } from '../lib/settle'
import { staggerStyle } from '../motion-react'
import {
  useFileExpense,
  useGroup,
  useGroupTransactions,
  useMoveProjectTransactions,
  useProject,
  useProjectBalances,
  useProjects,
  useProjectSettlementPlan,
  useProjectTransactions,
  useRenameProject,
  useSetProjectArchived,
} from '../queries'
import type { Route } from '../routes'

export function Project({
  groupId,
  projectId,
  myId,
  navigate,
}: {
  groupId: string
  projectId: string
  myId: string
  navigate: (to: Route) => void
}) {
  const group = useGroup(groupId)
  const project = useProject(groupId, projectId)
  const projects = useProjects(groupId)
  const inProject = useProjectTransactions(groupId, projectId)
  const groupTransactions = useGroupTransactions(groupId)

  // The project's own answers, not the group's — see the header.
  const balances = useProjectBalances(groupId, projectId)
  const simplify = group.data?.group.simplifyDebts ?? true
  const plan = useProjectSettlementPlan(groupId, projectId, group.data !== undefined, simplify)
  const fileExpense = useFileExpense(groupId, myId)

  const rename = useRenameProject(groupId, projectId)
  const setArchived = useSetProjectArchived(groupId, projectId)
  const move = useMoveProjectTransactions(groupId, projectId)

  const [renaming, setRenaming] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  const [ticked, setTicked] = useState<Set<string>>(() => new Set())
  /** Archive has been pressed once on an unsettled project and is waiting for the second. */
  const [confirmingArchive, setConfirmingArchive] = useState(false)
  /** Which row's Remove is in flight, so only that row says so. */
  const [removing, setRemoving] = useState<string | null>(null)
  /** The suggested payment whose Settle up dialog is open — as `Group` holds it. */
  const [settling, setSettling] = useState<Payment | null>(null)

  const back = () => navigate({ name: 'group', groupId })
  const current = project.data?.project
  const groupArchived = group.data?.group.archivedAt != null
  const locked = current?.archivedAt != null
  const writable = current !== undefined && !locked && !groupArchived
  const allProjects = projects.data?.projects ?? []
  const rows = pickerRows(groupTransactions.data?.transactions ?? [], allProjects, projectId)
  const chosen = tickedIds(rows, ticked)
  const busy = rename.isPending || setArchived.isPending || move.isPending
  const currency = group.data?.group.currency ?? balances.data?.group.currency ?? 'USD'
  const members = group.data?.members ?? []
  const settle = current ? projectSettleUp(current, groupArchived) : null

  if (project.isError) {
    return (
      <section className="card">
        <h2>That project</h2>
        <p className="bad">{project.error.message}</p>
        <p className="actions">
          <button type="button" onClick={back}>
            Back to the group
          </button>
        </p>
      </section>
    )
  }

  function submitRename(event: FormEvent) {
    event.preventDefault()
    if (renaming === null || projectNameProblem(renaming, allProjects, projectId) !== null) return
    rename.mutate(renaming.trim(), { onSuccess: () => setRenaming(null) })
  }

  function archive() {
    if (!current) return
    if (archiveWarning(current) !== null && !confirmingArchive) {
      setConfirmingArchive(true)
      return
    }
    setConfirmingArchive(false)
    setArchived.mutate(true)
  }

  function addTicked() {
    if (chosen.length === 0) return
    move.mutate(
      { transactionIds: chosen, attach: true },
      {
        onSuccess: () => {
          setTicked(new Set())
          setAdding(false)
        },
      },
    )
  }

  const renameProblem = renaming === null ? null : projectNameProblem(renaming, allProjects, projectId)
  const failure = rename.error ?? setArchived.error ?? move.error

  return (
    <>
      <p className="crumbs">
        <button type="button" className="button--quiet" onClick={back}>
          ← {group.data?.group.name ?? 'Group'}
        </button>
      </p>

      <section className="card">
        {renaming === null ? (
          <h2>{current?.name ?? 'Loading…'}</h2>
        ) : (
          <form className="form" onSubmit={submitRename}>
            <div className="form__row">
              <label htmlFor={`rename-${projectId}`}>Call it</label>
              <input
                id={`rename-${projectId}`}
                value={renaming}
                maxLength={MAX_PROJECT_NAME}
                required
                autoFocus
                onChange={(event) => setRenaming(event.target.value)}
              />
            </div>
            <p className="actions actions--tight">
              <button type="submit" disabled={renameProblem !== null || rename.isPending}>
                {rename.isPending ? 'Saving…' : 'Rename'}
              </button>
              <button
                type="button"
                className="button--quiet"
                disabled={rename.isPending}
                onClick={() => setRenaming(null)}
              >
                Cancel
              </button>
              {renameProblem && <span className="muted note">{renameProblem}</span>}
            </p>
          </form>
        )}

        {current && (
          <p className="project__summary">
            <span className="muted">{describeProjectCount(current.transactionCount)}</span>
            {locked && <span className="pill">Archived</span>}
            {current.settled && current.transactionCount > 0 && (
              <span className="pill pill--confirmed">
                <span aria-hidden="true">✓</span>
                Settled
              </span>
            )}
          </p>
        )}
        {locked && (
          <p className="badge">
            Archived — it is locked: nothing goes in or comes out until it is unarchived.
          </p>
        )}

        {current && !groupArchived && (
          <p className="actions">
            {!locked && renaming === null && (
              <button type="button" disabled={busy} onClick={() => setRenaming(current.name)}>
                Rename
              </button>
            )}
            {locked ? (
              <button type="button" disabled={busy} onClick={() => setArchived.mutate(false)}>
                {setArchived.isPending ? 'Unarchiving…' : 'Unarchive'}
              </button>
            ) : (
              <button
                type="button"
                className={confirmingArchive ? 'button--danger' : undefined}
                disabled={busy}
                onClick={archive}
              >
                {setArchived.isPending
                  ? 'Archiving…'
                  : confirmingArchive
                    ? 'Archive anyway — press again'
                    : 'Archive'}
              </button>
            )}
            {confirmingArchive && (
              <button
                type="button"
                className="button--quiet"
                onClick={() => setConfirmingArchive(false)}
              >
                Keep it open
              </button>
            )}
          </p>
        )}
        {confirmingArchive && current && (
          <p className="bad note" role="alert">
            {archiveWarning(current)}
          </p>
        )}
        {failure && <p className="bad note">{failure.message}</p>}
      </section>

      <section className="card">
        <h2>Where everybody stands</h2>
        <ul className="chips">
          {balances.data?.balances.map((row, index) => {
            const said = describeMember(row.displayName, row.amountCents, currency, row.userId === myId)
            return (
              <li key={row.userId} style={staggerStyle(index)}>
                <AmountChip
                  direction={said.direction}
                  label={said.label}
                  cents={said.cents}
                  currency={currency}
                  name={row.userId === myId ? 'You' : row.displayName}
                />
                <span className="visually-hidden">
                  {said.sentence} in this project
                  {row.left ? ' (has left the group)' : ''}
                </span>
              </li>
            )
          })}
        </ul>
        {balances.isPending && <p className="muted">Working out where everybody stands…</p>}
        {balances.isError && <p className="bad">{balances.error.message}</p>}

        {/*
          The project's plan, from the project's own endpoint. Settle up is
          only drawn while it can be pressed: an archived project is locked,
          and the line under the list says to unarchive it instead.
        */}
        <div className="plan">
          <h3>Who pays whom</h3>
          {plan.isPending && group.data !== undefined && <p className="muted">Working it out…</p>}
          {plan.isError && <p className="bad">{plan.error.message}</p>}
          <ol className="plan__list">
            {plan.data?.payments.map((payment, index) => (
              <li key={`${payment.fromUserId}-${payment.toUserId}`} style={staggerStyle(index)}>
                <span className="plan__who">
                  <strong>
                    {payment.fromUserId === myId
                      ? 'You'
                      : (payment.fromDisplayName ?? payment.fromUserId)}
                  </strong>{' '}
                  {payment.fromUserId === myId ? 'pay' : 'pays'}{' '}
                  <strong>
                    {payment.toUserId === myId ? 'you' : (payment.toDisplayName ?? payment.toUserId)}
                  </strong>
                </span>
                <span className="plan__amount">{formatMoney(payment.amountCents, currency)}</span>
                {settle?.canSettle && (
                  <button
                    type="button"
                    className="plan__settle"
                    disabled={fileExpense.isPending}
                    aria-haspopup="dialog"
                    onClick={() => setSettling(payment)}
                  >
                    Settle up
                    <span className="visually-hidden">
                      {' '}
                      this payment of {formatMoney(payment.amountCents, currency)} for{' '}
                      {current?.name}
                    </span>
                  </button>
                )}
              </li>
            ))}
          </ol>
          {plan.data && plan.data.payments.length > 0 && (
            <p className="muted note">
              {describePlanShape(plan.data.summary)} A payment settled here is recorded as being
              for this project, and counts in the group’s balances too.
            </p>
          )}
          {settle?.note && (
            <p className={current?.settled ? 'badge' : 'muted note'}>{settle.note}</p>
          )}
          {fileExpense.isError && <p className="bad note">{fileExpense.error.message}</p>}
        </div>
      </section>

      {/*
        The group's dialog, opened on the project's suggestion. The project is
        fixed rather than offered — the amount is what *this* project owes —
        and the draft carries its id, so the payment is tagged to it.
      */}
      {settling !== null && current && (
        <SettleUp
          key={`${settling.fromUserId}-${settling.toUserId}`}
          payment={settling}
          currency={currency}
          myId={myId}
          busy={fileExpense.isPending}
          project={current}
          onClose={() => setSettling(null)}
          onSettle={(settled, forProject) => {
            fileExpense.mutate({
              draft: settlementDraft({
                fromUserId: settling.fromUserId,
                toUserId: settling.toUserId,
                toDisplayName: settling.toDisplayName,
                amountCents: settled.amountCents,
                occurredOn: settled.occurredOn,
                projectId: forProject,
              }),
              members,
              idempotencyKey: newIdempotencyKey(),
            })
            setSettling(null)
          }}
        />
      )}

      <section className="card">
        <div className="card__head">
          <h2>In this project</h2>
          {writable && !adding && (
            <button type="button" disabled={busy} onClick={() => setAdding(true)}>
              Add transactions
            </button>
          )}
        </div>

        {adding && (
          <div className="sheet">
            <TransactionPicker
              legend="The group’s recent transactions"
              rows={rows}
              ticked={ticked}
              onChange={setTicked}
              disabled={move.isPending}
            />
            <p className="actions">
              <button
                type="button"
                disabled={chosen.length === 0 || move.isPending}
                onClick={addTicked}
              >
                {move.isPending && removing === null
                  ? 'Adding…'
                  : chosen.length > 0
                    ? `Add ${chosen.length}`
                    : 'Add'}
              </button>
              <button
                type="button"
                className="button--quiet"
                disabled={move.isPending}
                onClick={() => {
                  setAdding(false)
                  setTicked(new Set())
                }}
              >
                Cancel
              </button>
            </p>
          </div>
        )}

        {inProject.isPending && <p className="muted">Fetching the list…</p>}
        {inProject.isError && <p className="bad">{inProject.error.message}</p>}
        {inProject.data?.transactions.length === 0 && (
          <p className="muted">Nothing is in this project yet.</p>
        )}

        <ul className="rows">
          {inProject.data?.transactions.map((row) => {
            const stake = describeYourStake(myId, row.paid, row.owed, row.currency)
            return (
              <li key={row.id} className="row row--project">
                <span className="row__day">{formatDay(row.occurredOn)}</span>
                <span className="row__what">
                  <span className="row__description">
                    {row.description}
                    {row.kind !== 'expense' && (
                      <span className="pill">{row.kind === 'loan' ? 'loan' : 'settling up'}</span>
                    )}
                  </span>
                  <span className="row__who">{formatMoney(row.amountCents, row.currency)}</span>
                </span>
                <Amount
                  direction={stake.direction}
                  label={stake.label}
                  cents={stake.cents}
                  currency={row.currency}
                  count={false}
                />
                {writable && (
                  <button
                    type="button"
                    className="button--quiet row__edit"
                    disabled={busy}
                    onClick={() => {
                      setRemoving(row.id)
                      move.mutate(
                        { transactionIds: [row.id], attach: false },
                        { onSettled: () => setRemoving(null) },
                      )
                    }}
                  >
                    {removing === row.id ? 'Removing…' : 'Remove'}
                    <span className="visually-hidden"> {row.description} from this project</span>
                  </button>
                )}
              </li>
            )
          })}
        </ul>
        {inProject.data?.nextCursor && (
          <p className="muted note">Showing the most recent {inProject.data.transactions.length}.</p>
        )}
      </section>
    </>
  )
}
